import { useCallback, useEffect, useState } from 'react'

import { Button } from '../components/Button.js'
import { useServices } from '../services/ServicesProvider.js'
import { useServiceWorker } from './ServiceWorkerProvider.js'
import styles from './Prompts.module.css'

/** Set once the offer has been made, so declining is not re-asked every launch. */
export const INSTALL_OFFERED_KEY = 'install.offered'

/**
 * The Chromium-only event that lets a site trigger the install flow itself.
 *
 * Not in TypeScript's DOM library because it is not standardised. Safari has no
 * equivalent at all — there, installing is Share → Add to Home Screen and this
 * prompt simply never appears, which is the correct outcome rather than a gap
 * to paper over with instructions nobody follows.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/**
 * Offers installation, once the app holds something worth protecting.
 *
 * Installing is a **durability** feature here, not a convenience: per ADR-0003
 * Safari evicts script-writable storage after roughly seven days without
 * interaction for sites that are not on the Home Screen. So the copy says what
 * it actually buys rather than "add to home screen for quick access".
 *
 * It waits for real data deliberately. Asked on first launch, with nothing
 * stored, the offer is both easy to decline and untrue — there is nothing to
 * protect yet — and a declined prompt does not come back.
 */
export function InstallPrompt() {
  const { storage } = useServices()
  const { updateReady } = useServiceWorker()
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [eligible, setEligible] = useState(false)

  useEffect(() => {
    function capture(raw: Event) {
      // Chromium shows its own mini-infobar unless this is prevented, and two
      // competing prompts is worse than either.
      raw.preventDefault()
      setEvent(raw as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', capture)
    return () => window.removeEventListener('beforeinstallprompt', capture)
  }, [])

  useEffect(() => {
    let cancelled = false
    async function check() {
      const [alreadyOffered, entries] = await Promise.all([
        storage.settings.get(INSTALL_OFFERED_KEY),
        storage.entries.count(),
      ])
      if (!cancelled) {
        setEligible(alreadyOffered !== 'true' && entries > 0)
      }
    }
    void check()
    return () => {
      cancelled = true
    }
  }, [storage])

  const close = useCallback(async () => {
    setEligible(false)
    await storage.settings.set(INSTALL_OFFERED_KEY, 'true')
  }, [storage])

  const install = useCallback(async () => {
    const pending = event
    setEligible(false)
    await storage.settings.set(INSTALL_OFFERED_KEY, 'true')
    await pending?.prompt()
  }, [event, storage])

  // The update prompt wins. Both occupy the same corner, and one is about
  // correctness while this one is about convenience — so this waits rather than
  // stacking two decisions on top of each other.
  if (!eligible || !event || updateReady) {
    return null
  }

  return (
    <div className={styles['prompt']} role="status" aria-live="polite">
      <p className={styles['title']}>Install anytracker?</p>
      <p className={styles['body']}>
        Browsers delete data for sites you have not opened in a while. Installing this to
        your Home Screen makes that far less likely, and it opens without browser chrome.
      </p>
      <div className={styles['actions']}>
        <Button onClick={() => void close()}>No thanks</Button>
        <Button variant="primary" onClick={() => void install()}>
          Install
        </Button>
      </div>
    </div>
  )
}
